// Cost center picker — 2026-09-05
// Cascading picker: Kind → Parent → Sub. Parents whose kind requires a sub (collection, client) can only be
// chosen through one of their subs; parents whose kind allows it (project, other) also offer a
// "Whole <parent>" leaf. Search matches name, code and reference across the whole path.
// The list is fetched once per page load and shared by every instance (many MR lines use it).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Cascader } from 'antd';
import type { CascaderProps } from 'antd';
import { listAllocCostCenters, listCostCenterTypes, CostCenterRow, CostCenterType, CostCenterRef, costCenterLabel } from '../api';

let cache: { at: number; rows: CostCenterRow[]; types: CostCenterType[] } | null = null;
let inflight: Promise<{ rows: CostCenterRow[]; types: CostCenterType[] }> | null = null;
const TTL_MS = 60 * 1000;

export async function loadCostCenterCache(force = false) {
  if (!force && cache && Date.now() - cache.at < TTL_MS) return cache;
  if (!inflight) {
    inflight = Promise.all([listAllocCostCenters(false), listCostCenterTypes()])
      .then(([rows, types]) => {
        cache = { at: Date.now(), rows, types };
        return cache;
      })
      .finally(() => { inflight = null; });
  }
  return inflight;
}

export function invalidateCostCenterCache() {
  cache = null;
}

export function useCostCenters(force = false) {
  const [state, setState] = useState<{ rows: CostCenterRow[]; types: CostCenterType[]; loading: boolean }>({
    rows: cache?.rows || [], types: cache?.types || [], loading: !cache,
  });
  useEffect(() => {
    let alive = true;
    loadCostCenterCache(force).then((c) => { if (alive) setState({ rows: c.rows, types: c.types, loading: false }); }).catch(() => { if (alive) setState((s) => ({ ...s, loading: false })); });
    return () => { alive = false; };
  }, [force]);
  return state;
}

type Opt = { value: string | number; label: React.ReactNode; plain: string; searchText: string; ccId?: number; children?: Opt[] };

const KIND = (code: string) => `kind:${code}`;
const SELF = (id: number) => `self:${id}`;

interface Props extends Omit<CascaderProps<any>, 'options' | 'value' | 'onChange' | 'multiple'> {
  value?: number | null;
  onChange?: (id: number | null, cc: CostCenterRef | null) => void;
  /** When the current value is inactive / not in the picker (old lines), render it from this object. */
  current?: CostCenterRef | null;
}

const Code: React.FC<{ code: string }> = ({ code }) => <span style={{ color: '#aaa', marginLeft: 6, fontSize: 11 }}>{code}</span>;

const CostCenterSelect: React.FC<Props> = ({ value, onChange, current, placeholder, style, ...rest }) => {
  const { rows, types, loading } = useCostCenters();

  const { options, pathById } = useMemo(() => {
    const typeMap = new Map(types.map((t) => [t.code, t]));
    const parentTypes = types.filter((t) => !t.parentTypeCode && !t.anyParent).sort((a, b) => a.sortOrder - b.sortOrder);
    const ids = new Set(rows.map((r) => r.id));
    const byParent = new Map<number, CostCenterRow[]>();
    for (const r of rows) {
      if (r.parent && ids.has(r.parent.id)) { // subtrees of an inactive node stay hidden (only active rows are loaded)
        if (!byParent.has(r.parent.id)) byParent.set(r.parent.id, []);
        byParent.get(r.parent.id)!.push(r);
      }
    }
    const pathById = new Map<number, Array<string | number>>();
    const options: Opt[] = [];

    // Recursive node → option. Returns null when the node must not be offered (kind requires a sub and none is active).
    const build = (node: CostCenterRow, path: Array<string | number>, labelPath: string[]): Opt | null => {
      const kind = typeMap.get(node.typeCode);
      const kids = (byParent.get(node.id) || []).map((k) => build(k, [...path, node.id], [...labelPath, node.name])).filter((x): x is Opt => !!x);
      if (kind?.childRequired && kids.length === 0) return null;
      const here = [...path, node.id];
      const plain = [...labelPath, node.name].join(' › ');
      const opt: Opt = {
        value: node.id,
        ccId: node.id,
        label: <span>{node.name}<Code code={node.code} /></span>,
        plain,
        searchText: `${plain} ${node.code} ${node.externalRef || ''}`.toLowerCase(),
      };
      if (kids.length > 0) {
        opt.children = [];
        if (!kind?.childRequired) {
          opt.children.push({ value: SELF(node.id), ccId: node.id, label: <em>Whole {node.name}</em>, plain, searchText: `${plain} ${node.code} whole general`.toLowerCase() });
          pathById.set(node.id, [...here, SELF(node.id)]);
        }
        opt.children.push(...kids);
      } else {
        pathById.set(node.id, here);
      }
      return opt;
    };

    for (const kind of parentTypes) {
      const roots = rows.filter((r) => (!r.parent || !ids.has(r.parent.id)) && r.typeCode === kind.code);
      const kindOpt: Opt = { value: KIND(kind.code), label: kind.label, plain: kind.label, searchText: '', children: [] };
      for (const root of roots) {
        const o = build(root, [kindOpt.value], []);
        if (o) kindOpt.children!.push(o);
      }
      if (kindOpt.children!.length > 0) options.push(kindOpt);
    }

    // Current value not in the active list (inactive or removed): show it so old lines still render.
    if (value && current && !pathById.has(value)) {
      const lbl = costCenterLabel(current);
      options.unshift({ value: 'kind:__current', label: 'Current (inactive)', plain: 'Current', searchText: '', children: [{ value, ccId: value, label: <span style={{ color: '#999' }}>{lbl} (inactive)</span>, plain: lbl, searchText: lbl.toLowerCase() }] });
      pathById.set(value, ['kind:__current', value]);
    }
    return { options, pathById };
  }, [rows, types, value, current]);

  const path = value ? pathById.get(value) : undefined;

  // Deep trees open more panels than fit on screen: keep the newest panel in view.
  const wrapRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  useEffect(() => {
    if (!open) return;
    const el = wrapRef.current;
    if (!el) return;
    const toEnd = () => { const m = el.querySelector('.ant-cascader-menus') as HTMLElement | null; if (m) m.scrollLeft = m.scrollWidth; };
    const obs = new MutationObserver(toEnd);
    obs.observe(el, { childList: true, subtree: true });
    toEnd();
    return () => obs.disconnect();
  }, [open]);

  return (
    <Cascader
      {...rest}
      onDropdownVisibleChange={(v) => { setOpen(v); if (!v) setSearching(false); rest.onDropdownVisibleChange?.(v); }}
      onSearch={(v) => setSearching(!!v)}
      dropdownRender={(menus) => <div ref={wrapRef} className={searching ? 'cc-cascader-searching' : undefined}>{menus}</div>}
      options={options as any}
      value={path as any}
      loading={loading}
      allowClear
      expandTrigger="click"
      placeholder={placeholder || 'Project / collection / client › sub'}
      style={{ minWidth: 180, width: '100%', ...(style || {}) }}
      popupClassName="cc-cascader"
      placement="bottomRight"
      displayRender={(_labels: any, selected: any) => {
        const leaf = selected?.[selected.length - 1];
        return leaf?.plain || (current ? costCenterLabel(current) : '');
      }}
      showSearch={{
        filter: (input: string, p: any[]) => {
          const q = input.toLowerCase();
          return p.some((o) => (o.searchText || '').includes(q));
        },
        render: (_input: string, p: any[]) => {
          const leaf = p[p.length - 1];
          const cc = rows.find((r) => r.id === leaf?.ccId);
          const isSelf = typeof leaf?.value === 'string' && leaf.value.startsWith('self:');
          const parts: string[] = (leaf?.plain || '').split(' › ');
          const last = parts.pop();
          return (
            <span>
              {parts.length ? <span style={{ color: '#888' }}>{parts.join(' › ')} › </span> : null}
              {isSelf ? <em>Whole {last}</em> : last}
              {cc ? <Code code={cc.code} /> : null}
            </span>
          );
        },
        limit: 50,
      }}
      onChange={(_vals: any, selected: any) => {
        const leaf = selected?.[selected.length - 1];
        const id: number | null = leaf?.ccId ?? null;
        const cc = id ? rows.find((r) => r.id === id) || (current && current.id === id ? current : null) : null;
        onChange?.(id, cc as CostCenterRef | null);
      }}
    />
  );
};

export default CostCenterSelect;
